/*
 * @Description:
 * @Author: fiveonelei
 * @Date: 2022-03-16 12:10:48
 * @FilePath: /app-monorepo-projects/packages/app-share/src/utils/script.ts
 */
import { IHashMap } from '@/types/data';
import { loadWslSchemaAsync } from '@/utils/load-wslib';
import { retryPromiseFn } from '@/utils/common';

/**
 * 插入js脚本
 */
export const insertJs = (src: string) => new Promise((resolve, reject) => {
  if (typeof document === 'undefined') {
    resolve(undefined);
    return;
  };

  const tag = document.createElement('script');
  tag.type = 'text/javascript';
  tag.src = src;

  const head = document?.getElementsByTagName('head')?.[0];
  if (!head) return;

  head.appendChild(tag);
  tag.addEventListener('load', resolve);
  tag.addEventListener('error', (arg) => {
    head.removeChild(tag);
    reject(arg);
  });
});

type TSwiperPromise = ReturnType<typeof getSwiper>;
let swiperPromise: TSwiperPromise | undefined;
/**
 * 获取 Swiper
 *
 * @return {*}  {(Promise<Window['Swiper'] | null>)}
 */
export const getSwiper = async (): Promise<Window['Swiper'] | null> => {
  if (typeof window === undefined) return null;
  if (window.Swiper) return window.Swiper;

  let promise;
  if (swiperPromise) {
    promise = swiperPromise;
  } else {
    promise = insertJs('//isee.weishi.qq.com/nativechannel/js/swiper-bundle.min.js') as TSwiperPromise;
    swiperPromise = promise;
  }

  try {
    await promise;
    console.log('[getSwiper] 加载成功');
    return window.Swiper;
  } catch (error) {
    console.error('[getSwiper] 加载失败，error: ', error);
    swiperPromise = undefined;
  }
  return null;
};


export const isWSInstall = async (schema?: string) => {
  const wsSchema = await loadWslSchemaAsync();
  if (!wsSchema) return;
  return new Promise(async (resolve) => {
    await wsSchema.load();
    const opt: IHashMap = {
      cb: (res: string) => (res === 'yes' ? resolve(true) : resolve(false)),
    };
    if (schema) {
      opt.schema = schema;
    }
    window?.openDownloadApp?.isAppInstall(opt);
  });
};

/**
 * 获取 web-tab-sdk
 */
type TABPromise = ReturnType<typeof getTABSdk>;
let tabPromise: TABPromise | undefined;;
export const getTABSdk = async <T>(): Promise<T | null> => {
  if (typeof window === undefined) return null;
  // @ts-ignore
  if (window?.wabt) return window?.wabt;

  let promise;
  if (tabPromise) {
    promise = tabPromise;
  } else {
    promise = insertJs('//res.ab.qq.com/tab/abt_jssdk.min.js');
    tabPromise = promise;
  }

  try {
    await promise;
    console.log('[getTABSdk] 加载成功');
    // @ts-ignore
    return window?.wabt;
  } catch (error) {
    console.error('[getTABSdk] 加载失败，error: ', error);
    tabPromise = undefined;
  }
  return null;
};

type TAegisPromise = ReturnType<typeof getAegis>;
let aegisPromise: TAegisPromise | undefined;
/**
 * 获取 Aegis
 *
 * @return {*}  {(Promise<Window['Aegis'] | null>)}
 */
export const getAegis = async (): Promise<Window['Aegis'] | null> => {
  if (typeof window === undefined) return null;
  if (window.Aegis) return window.Aegis;

  let promise;
  if (aegisPromise) {
    promise = aegisPromise;
  } else {
    promise = insertJs('//isee.weishi.qq.com/vise-public/aegis/2201280830/aegis.min.js') as TAegisPromise;
    aegisPromise = promise;
  }

  try {
    await promise;
    console.log('[getAegis] 加载成功');
    return window.Aegis;
  } catch (error) {
    console.error('[getAegis] 加载失败，error: ', error);
    aegisPromise = undefined;
  }
  return null;
};

/**
 * 带重试机制地获取 Aegis
 *
 * @param {number} [retryTimes=3]
 * @return {*} {(Promise<Window['Aegis'] | null>)}
 */
export const getAegisWithRetry = async (retryTimes = 3): Promise<Window['Aegis'] | null> => {
  let aegisClass = null;
  try {
    aegisClass = await retryPromiseFn(getAegis, retryTimes);
  } catch (err) {
    console.error('[getAegisWithRetry] 执行失败，error: ', err);
  }
  return aegisClass;
};

type TMqqPromise = ReturnType<typeof getWsmqq>;
let mqqPromise: TMqqPromise | undefined;
/**
 * 获取 mqq
 */
export const getWsmqq = async (): Promise<Window['mqq'] | null> => {
  if (typeof window === 'undefined') return null;
  if (window.mqq) return window.mqq;

  let promise;
  if (mqqPromise) {
    promise = mqqPromise;
  } else {
    promise = insertJs('//isee.weishi.qq.com/sdk/jsb/wsapi.js') as TMqqPromise;
    mqqPromise = promise;
  }

  try {
    await promise;
    console.log('[getWsmqq] 加载成功');
    return window.mqq;
  } catch (err) {
    console.error('[getWsmqq] 加载失败，error: ', err);
    mqqPromise = undefined;
  }

  return null;
};

/**
 * 带重试机制地获取 wsmqq
 * - 返回为 null 时，说明获取失败
 */
export const getWsmqqWithRetry = async (retryTimes = 3) => {
  let mqq = null;
  try {
    mqq = await retryPromiseFn(async () => {
      const result = await getWsmqq();
      if (!result) throw new Error('获取失败');
      return result;
    }, retryTimes);
  } catch (err) {
    console.error('[getWsmqqWithRetry] 执行失败，error: ', err);
  }
  return mqq;
};
